import { Body, H1 } from '../shared'
import NavBar from '../NavBar'
import { useNavigate } from 'react-router-dom'
import { RoutePath } from '../../constants'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRight } from '@fortawesome/free-solid-svg-icons/faArrowRight'
import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus'
import Button from '../shared/Button'

interface AboutProps {}

const BUTTON_MOBILE_WIDTH = '340px'

const About = (_props: AboutProps) => {
  const navigate = useNavigate()
  return (
    <section
      id="about"
      className="bg-white min-h-screen h-full flex flex-col px-8 gap-8"
    >
      <NavBar theme="dark" />
      <div className="relative flex flex-grow pb-8 flex-col max-w-[981px] m-auto">
        <div className="relative flex flex-col gap-8">
          <H1>About this map</H1>
          <Body>
            With the growing crescendo of calls for reparations and redress of
            systemic injustices against Black people in the United States we’ve
            designed this tool to expedite these efforts.{' '}
          </Body>
          <Body>
            This map has a two-fold purpose: (a) to map and identify the
            collective organizations involved in reparations, and initiatives
            underway, fostering a stronger network through knowledge exchange,
            and (b) to enlighten and engage movement partners, policymakers,
            funders, and other key stakeholders about the numerous avenues to
            support reparations across the nation.{' '}
          </Body>
          <Body>
            This map is intended to be a comprehensive view of the U.S.
            reparations ecosystem. However, we acknowledge the map may not
            encapsulate every reparations organization or initiative in America.
            As new organizations are identified, they will be added to the
            database. For additional questions about the map, please reach out
            to us at{' '}
            <a
              className="text-green font-semibold"
              href="mailto:map@firstrepair.org"
            >
              map@firstrepair.org
            </a>
            .
          </Body>
          <Body>
            To learn more about how to request the addition of an organization
            or initiative,{' '}
            <a href={'/add-organization'} className="text-green underline">
              click here.
            </a>
          </Body>
          <div className="hidden sm:flex sm:gap-16">
            <Button onClick={() => navigate(RoutePath.AddOrganization)}>
              <FontAwesomeIcon icon={faPlus} />
              <p className="text-[16px] uppercase flex-1 font-bold">
                Organization or Initiative
              </p>
              <FontAwesomeIcon icon={faArrowRight} />
            </Button>
            <Button onClick={() => navigate(RoutePath.Map)}>
              <p className="text-[16px] uppercase flex-1 font-bold">View Map</p>
              <FontAwesomeIcon icon={faArrowRight} />
            </Button>
          </div>
          <div className="sm:hidden flex flex-col gap-8">
            <Button
              onClick={() => navigate(RoutePath.AddOrganization)}
              styles={{ width: BUTTON_MOBILE_WIDTH }}
            >
              <FontAwesomeIcon icon={faPlus} />
              <p className="text-[16px] uppercase flex-1 font-bold">
                Organization or Initiative
              </p>
              <FontAwesomeIcon icon={faArrowRight} />
            </Button>
            <Button
              onClick={() => navigate(RoutePath.Map)}
              styles={{ width: BUTTON_MOBILE_WIDTH }}
            >
              <p className="text-[16px]  uppercase flex-1 font-bold">
                View Map
              </p>
              <FontAwesomeIcon icon={faArrowRight} />
            </Button>
          </div>
          <h4 className="uppercase font-decimal font-bold text-[20px] mb-[-24px] mt-4">
            About FirstRepair
          </h4>
          <Body>
            FirstRepair envisions a just society where Black people are
            liberated, thriving, and repaired. FirstRepair supports community
            organizations, local government and other relevant institutions and
            bodies in successfully implementing tangible reparations through
            local efforts. We do this by synchronizing and equipping local
            leaders; building a national movement for local reparations; and
            demonstrating life with repair. Learn more about our work at{' '}
            <a
              href={'https://www.firstrepair.org'}
              className="text-green underline"
            >
              www.firstrepair.org
            </a>
            .
          </Body>
          <Body>
            Founded in 2021, FirstRepair is led by Robin Rue Simmons, former
            Evanston, Illinois alderman and the architect of the nation’s first
            government-funded reparations program. Evanston’s example has
            inspired and encouraged over 100 local initiatives working towards
            the same goal, where local leaders and stakeholders are urgently
            advancing local legislation.
          </Body>
          <Body>
            In our first four years, FirstRepair has provided support to more
            than 76 communities engaged in local initiatives; established the
            National Symposium for State and Local Reparations, the signature
            convening for the local reparations movement that coalesces over 200
            local leaders; and build four regional networks with local
            leadership. Engaged communities include Asheville, NC; Kansas City,
            MO; Tulsa, OK; Fayetteville, AR, and dozens more.
          </Body>

          <h4 className="uppercase font-decimal font-bold text-[20px] mb-[-24px] mt-4">
            Founding Funder and Creator: Decolonizing Wealth Project
          </h4>
          <Body>
            Decolonizing Wealth Project (DWP) is committed to bringing truth,
            healing, and repair to the global community. Established in 2018,
            and led by Edgar Villanueva, an Indigenous, award-winning author,
            and expert on wealth, spirituality, and social justice, DWP operates
            through three key strategies: sector transformation, storytelling
            and culture, and reparative giving.
          </Body>

          <Body>
            DWP’s work has radically transformed the philanthropic sector and
            has facilitated the distribution of over $700 million for social
            justice efforts. Liberated Capital, DWP’s fund and donor community,
            has granted over $21 million to support economic solidarity,
            wellbeing, and earth and climate efforts primarily led by Black and
            Indigenous communities.
          </Body>
          <h4 className="uppercase font-decimal font-bold text-[20px] mb-[-16px] mt-4">
            Data and Research Partners 
          </h4>
          <a
            href="https://www.bu.edu/antiracism-center/"
            className="text-green underline"
            target="_blank"
            rel="noreferrer"
          >
            Boston University — Center for Antiracist Research
          </a>
          <a
            href={'https://www.epi.org/'}
            target="_blank"
            rel="noreferrer"
            className="text-green underline"
          >
            Economic Policy Institute
          </a>
          <a
            href={'https://www.hks.harvard.edu/faculty/cornell-william-brooks'}
            className="text-green underline"
            target="_blank"
            rel="noreferrer"
          >
            Dr. Cornell Brooks, Harvard Kenney School
          </a>
        </div>
      </div>
    </section>
  )
}
export default About
